import { Link } from "react-router-dom"
import type { Project, Task } from "../lib/api"
import { COLOR_DOT, DEFAULT_CONFIG, isOverdueIn, statusOf } from "../lib/boardConfig"
import { CALENDAR_DAY_MS, calendarMonthRange, utcDayKey } from "../lib/calendar"
import { Button } from "./ui"

// The third board view: the month, with tasks placed on their due dates.
//
// Placement is by due date alone. A task with a start date draws a thin run-up
// bar from start to due, but it stays listed only on its due day: a delivery
// calendar answers "what lands when", and repeating a chip across three weeks
// answers nothing while burying everything else.

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

export function CalendarView({
  tasks,
  projects,
  cursor,
  onCursorChange,
}: {
  tasks: Task[]
  projects: Project[]
  cursor: number
  onCursorChange: (cursor: number) => void
}) {
  const first = new Date(cursor)
  const { year, month, gridStart, weeks } = calendarMonthRange(cursor)
  const projectById = new Map(projects.map((project) => [project.id, project]))

  const byDay = new Map<string, Task[]>()
  for (const task of tasks) {
    if (!task.dueDate) continue
    const key = task.dueDate.slice(0, 10)
    byDay.set(key, [...(byDay.get(key) ?? []), task])
  }

  const today = utcDayKey(new Date())
  const monthName = first.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  })
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => onCursorChange(Date.UTC(year, month - 1, 1))} aria-label="Previous month">
          ‹
        </Button>
        <span className="min-w-36 text-center text-sm font-medium text-foreground">{monthName}</span>
        <Button variant="ghost" size="sm" onClick={() => onCursorChange(Date.UTC(year, month + 1, 1))} aria-label="Next month">
          ›
        </Button>
        <Button
          variant="subtle"
          size="sm"
          onClick={() => {
            const now = new Date()
            onCursorChange(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
          }}
        >
          Today
        </Button>
        <span className="ml-auto text-xs text-faint">All active projects</span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 gap-px">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-2 py-1 text-[11px] font-medium tracking-wide text-faint uppercase">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
            {Array.from({ length: weeks * 7 }, (_, i) => {
              const date = new Date(gridStart + i * CALENDAR_DAY_MS)
              const key = utcDayKey(date)
              const inMonth = date.getUTCMonth() === month
              const dayTasks = byDay.get(key) ?? []

              return (
                <div
                  key={key}
                  className={`flex min-h-24 flex-col gap-1 p-1.5 ${inMonth ? "bg-surface" : "bg-surface-muted"}`}
                >
                  <span
                    className={`self-end px-1 font-mono text-[11px] tabular-nums ${
                      key === today
                        ? "rounded bg-interactive font-semibold text-white"
                        : inMonth
                          ? "text-muted"
                          : "text-faint"
                    }`}
                  >
                    {date.getUTCDate()}
                  </span>
                  {dayTasks.map((task) => {
                    const project = projectById.get(task.projectId)
                    const config = project?.config ?? DEFAULT_CONFIG
                    const meta = statusOf(config, task.status)
                    const overdue = isOverdueIn(config, task.dueDate, task.status)
                    return (
                      <Link
                        key={task.id}
                        to={`/tasks/${task.id}`}
                        title={`${project?.name ?? task.projectName ?? "Project"} · ${task.key} · ${task.title}${meta.name ? ` · ${meta.name}` : ""}`}
                        className={`flex flex-col rounded border px-1.5 py-1 text-[11px] transition-colors hover:border-border-strong ${
                          overdue
                            ? "border-danger/40 bg-danger-soft text-danger"
                            : meta.category === "done"
                              ? "border-border bg-surface-muted text-faint line-through"
                              : "border-border bg-surface text-foreground"
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className={`size-1.5 shrink-0 rounded-full ${COLOR_DOT[meta.color] ?? COLOR_DOT.gray}`} />
                          <span className="truncate">
                            <span className="font-mono text-[10px] text-faint">{task.key}</span> {task.title}
                          </span>
                        </span>
                        <span className="truncate pl-3 text-[10px] text-faint">
                          {project?.name ?? task.projectName ?? task.projectKeyPrefix ?? "Project"}
                        </span>
                      </Link>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
